import { useEffect } from 'react';

const GTMLoader = () => {
  useEffect(() => {
    const loadGTMSettings = async () => {
      try {
        const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        const response = await fetch(`${API_BASE}/api/google-tag-manager`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Inject GTM Container ID script
          if (data.gtm_container_id && data.gtm_container_id.trim()) {
            injectGTMScript(data.gtm_container_id);
          }
          
          // Inject GA4 Measurement ID script
          if (data.ga4_measurement_id && data.ga4_measurement_id.trim()) {
            injectGA4Script(data.ga4_measurement_id);
          }
          
          // Inject custom conversion code
          if (data.conversion_code && data.conversion_code.trim()) {
            injectCustomCode(data.conversion_code, 'gtm-conversion-code');
          }

          // Inject Facebook Pixel
          if (data.fb_pixel_id && data.fb_pixel_id.trim()) {
            injectFacebookPixel(data.fb_pixel_id);
          }

          // Inject TikTok Pixel
          if (data.tiktok_pixel && data.tiktok_pixel.trim()) {
            injectTikTokPixel(data.tiktok_pixel);
          }

          // Inject custom header code
          if (data.custom_header_code && data.custom_header_code.trim()) {
            injectCustomCode(data.custom_header_code, 'gtm-custom-header-code');
          }
        }
      } catch (error) {
        console.error('Error loading GTM settings:', error);
      }
    };

    loadGTMSettings();
  }, []);

  const injectGTMScript = (containerId) => {
    // Check if script already exists
    if (document.querySelector(`script[src*="${containerId}"]`)) {
      return;
    }

    // Create gtag script
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${containerId}`;
    document.head.appendChild(gtagScript);

    // Create inline script
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${containerId}');
    `;
    document.head.appendChild(inlineScript);

    console.log('GTM Container ID injected:', containerId);
  };

  const injectGA4Script = (measurementId) => {
    // Check if script already exists
    if (document.querySelector(`script[src*="${measurementId}"]`)) {
      return;
    }

    // Create gtag script
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(gtagScript);

    // Create inline script
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `;
    document.head.appendChild(inlineScript);

    console.log('GA4 Measurement ID injected:', measurementId);
  };

  const injectCustomCode = (code, id) => {
    // Check if custom code already injected
    if (document.querySelector(`#${id}`)) {
      return;
    }

    // Create a wrapper div for custom code
    const wrapper = document.createElement('div');
    wrapper.id = id;
    wrapper.innerHTML = code;
    document.head.appendChild(wrapper);

    console.log(`Custom code injected: ${id}`);
  };

  const injectFacebookPixel = (pixelId) => {
    // Check if FB Pixel already exists
    if (document.querySelector('#fb-pixel-script')) {
      return;
    }

    // Create inline script for FB Pixel
    const fbScript = document.createElement('script');
    fbScript.id = 'fb-pixel-script';
    fbScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(fbScript);

    // Create noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" />`;
    document.head.appendChild(noscript);

    console.log('Facebook Pixel injected:', pixelId);
  };

  const injectTikTokPixel = (pixelId) => {
    // Check if TikTok Pixel already exists
    if (document.querySelector('#tiktok-pixel-script')) {
      return;
    }

    // Create inline script for TikTok Pixel
    const ttScript = document.createElement('script');
    ttScript.id = 'tiktok-pixel-script';
    ttScript.innerHTML = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
        ttq.load('${pixelId}');
        ttq.page();
      }(window, document, 'ttq');
    `;
    document.head.appendChild(ttScript);

    console.log('TikTok Pixel injected:', pixelId);
  };

  return null; // This component doesn't render anything
};

export default GTMLoader;
